import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface NotificationData {
  type: 'payroll_calculated' | 'payroll_approved' | 'payslip_ready';
  employee_id: string;
  employee_name: string;
  employee_email: string;
  period_month: number;
  period_year: number;
  gross_salary: number;
  net_salary: number;
  payslip_url?: string;
}

export class PayrollNotificationService {
  static async sendNotification(data: NotificationData): Promise<boolean> {
    try {
      const { error } = await supabase.functions.invoke('send-payroll-notification', {
        body: { data }
      });

      if (error) {
        console.error('Failed to send payroll notification:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error sending payroll notification:', error);
      return false;
    }
  }

  static async notifyPayrollCalculated(
    employeeId: string,
    employeeName: string,
    employeeEmail: string,
    periodMonth: number,
    periodYear: number,
    grossSalary: number,
    netSalary: number
  ): Promise<boolean> {
    return this.sendNotification({
      type: 'payroll_calculated',
      employee_id: employeeId,
      employee_name: employeeName,
      employee_email: employeeEmail,
      period_month: periodMonth,
      period_year: periodYear,
      gross_salary: grossSalary,
      net_salary: netSalary,
    });
  }

  static async notifyPayrollApproved(
    employeeId: string,
    employeeName: string,
    employeeEmail: string,
    periodMonth: number,
    periodYear: number,
    grossSalary: number,
    netSalary: number
  ): Promise<boolean> {
    return this.sendNotification({
      type: 'payroll_approved',
      employee_id: employeeId,
      employee_name: employeeName,
      employee_email: employeeEmail,
      period_month: periodMonth,
      period_year: periodYear,
      gross_salary: grossSalary,
      net_salary: netSalary,
    });
  }

  static async notifyPayslipReady(
    employeeId: string,
    employeeName: string,
    employeeEmail: string,
    periodMonth: number,
    periodYear: number,
    grossSalary: number,
    netSalary: number,
    payslipUrl?: string
  ): Promise<boolean> {
    return this.sendNotification({
      type: 'payslip_ready',
      employee_id: employeeId,
      employee_name: employeeName,
      employee_email: employeeEmail,
      period_month: periodMonth,
      period_year: periodYear,
      gross_salary: grossSalary,
      net_salary: netSalary,
      payslip_url: payslipUrl,
    });
  }

  static async bulkNotifyPayrollCalculated(
    calculations: Array<{
      employee_id: string;
      employees?: { name: string; email: string };
      gross_salary: number;
      net_salary: number;
    }>,
    periodMonth: number,
    periodYear: number
  ): Promise<{ success: number; failed: number }> {
    let successCount = 0;
    let failedCount = 0;

    for (const calc of calculations) {
      if (!calc.employees?.email) {
        failedCount++;
        continue;
      }

      const success = await this.notifyPayrollCalculated(
        calc.employee_id,
        calc.employees.name || 'Unknown',
        calc.employees.email,
        periodMonth,
        periodYear,
        calc.gross_salary,
        calc.net_salary
      );

      if (success) {
        successCount++;
      } else {
        failedCount++;
      }

      // Add small delay to avoid overwhelming the email service
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return { success: successCount, failed: failedCount };
  }

  static async bulkNotifyPayrollApproved(
    calculations: Array<{
      employee_id: string;
      employees?: { name: string; email: string };
      gross_salary: number;
      net_salary: number;
    }>,
    periodMonth: number,
    periodYear: number
  ): Promise<{ success: number; failed: number }> {
    let successCount = 0;
    let failedCount = 0;

    for (const calc of calculations) {
      if (!calc.employees?.email) {
        failedCount++;
        continue;
      }

      const success = await this.notifyPayrollApproved(
        calc.employee_id,
        calc.employees.name || 'Unknown',
        calc.employees.email,
        periodMonth,
        periodYear,
        calc.gross_salary,
        calc.net_salary
      );

      if (success) {
        successCount++;
      } else {
        failedCount++;
      }

      // Add small delay to avoid overwhelming the email service
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return { success: successCount, failed: failedCount };
  }
}

export function usePayrollNotifications() {
  const { toast } = useToast();

  const sendCalculatedNotifications = async (
    calculations: Array<{
      employee_id: string;
      employees?: { name: string; email: string };
      gross_salary: number;
      net_salary: number;
    }>,
    periodMonth: number,
    periodYear: number
  ) => {
    const result = await PayrollNotificationService.bulkNotifyPayrollCalculated(
      calculations,
      periodMonth,
      periodYear
    );

    toast({
      title: 'Notifications Sent',
      description: `${result.success} emails sent successfully. ${result.failed} failed.`,
      variant: result.failed > 0 ? 'destructive' : 'default',
    });

    return result;
  };

  const sendApprovedNotifications = async (
    calculations: Array<{
      employee_id: string;
      employees?: { name: string; email: string };
      gross_salary: number;
      net_salary: number;
    }>,
    periodMonth: number,
    periodYear: number
  ) => {
    const result = await PayrollNotificationService.bulkNotifyPayrollApproved(
      calculations,
      periodMonth,
      periodYear
    );

    toast({
      title: 'Approval Notifications Sent',
      description: `${result.success} emails sent successfully. ${result.failed} failed.`,
      variant: result.failed > 0 ? 'destructive' : 'default',
    });

    return result;
  };

  return {
    sendCalculatedNotifications,
    sendApprovedNotifications,
  };
}